<script setup lang="ts">
import { computed } from 'vue';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import InspectorDetailList from '@/components/InspectorDetailList.vue';
import InspectorDetailRow from '@/components/InspectorDetailRow.vue';
import {
  formatUpdateFieldsEntries,
  parseUpdateFields,
} from '@/components/HistoryInspector/updateFieldsFormatter';

/**
 * Popover that surfaces a `LiveTrackAction.update_fields` payload as a
 * definition-list of human labels + formatted values.
 *
 * Lives in the History Inspector card under the timestamp; trigger is a small
 * text button so the chrome stays quiet next to the action label / status pill.
 * The popover renders inline (no portaling) so it stacks correctly inside the
 * inspector slide-over.
 *
 * The trigger is hidden when the payload is empty / unparseable — the card
 * caller uses {@link parseUpdateFields} (or the same `hasEntries` computed
 * here, exported via `v-if`) to avoid rendering an empty popover affordance.
 */
const props = defineProps<{
  /** Raw `update_fields` value from the action; ES may hand it back as JSON string. */
  updateFields: unknown;
}>();

const entries = computed(() => formatUpdateFieldsEntries(parseUpdateFields(props.updateFields)));

const hasEntries = computed(() => entries.value.length > 0);
</script>

<template>
  <Popover v-if="hasEntries">
    <PopoverTrigger as-child>
      <button
        type="button"
        class="text-primary hover:underline text-[11px] leading-none font-medium"
        data-testid="history-action-card-updated-fields-trigger"
      >
        Show updated fields
      </button>
    </PopoverTrigger>
    <PopoverContent
      align="end"
      side="bottom"
      class="w-80 max-w-[90vw] p-3"
      data-testid="history-action-card-updated-fields-popover"
    >
      <div class="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Updated fields
      </div>
      <!--
        Definition-list semantics: `<dt>` is the field name, `<dd>` the value.
        The shared InspectorDetailList keeps labels left-aligned and lets the
        value fill the remaining column, wrapping long titles instead of
        truncating them into a sliver; `value-title` still surfaces the full
        string on hover.
      -->
      <InspectorDetailList class="gap-x-3 gap-y-1.5 text-xs">
        <InspectorDetailRow
          v-for="entry in entries"
          :key="entry.key"
          :label="entry.label"
          :value="entry.value"
          :value-title="entry.value"
          value-class="font-medium"
        />
      </InspectorDetailList>
    </PopoverContent>
  </Popover>
</template>
